import { FunctionComponent } from "react";
import { Handle, Position } from "reactflow";
import {
  COLORS,
  ResizableImage,
  SmallRegular,
} from "@bikdotai/bik-component-library";
import { CustomNodeProps, IconMapping } from "../../../utilities/flowBuilder.utility";
import { NodeStyle } from "../../flow-builder/style";
import { MessageNodeStyle } from "../../flow-builder/messageNode/style";
import {
  AttachmentGridContainer,
  AttachmentMainImage,
  AttachmentThumbnailGrid,
  AttachmentThumbnail,
  AddActionButton,
} from "../../flow-builder/messageNode/style";
import { ChildNodeContainer, ChildNodeText } from "../../flow-builder/style";
import { backgroundBasedOnSubType } from "../../flow-builder/util";
import { NodeIdHeader } from "../../flow-builder/messageNode/NodeIdHeader";
import { NodeSubType } from "../../../models/nodeSubType";
import { MacroChannelKey } from "../../../constants/MacroChannels";
import { AttachmentType } from "../../../components/responseBlock/types";
import VideoIcon from "../../../icons/video";
import DocumentIcon from "../../../icons/documentIcon";
import {
  AttachmentDivider,
  DocumentPreview,
  VideoIconOverlay,
  sourceHandleStyle,
} from "./style";

const MAX_THUMBNAIL_COLUMNS = 4;
const MAX_THUMBNAIL_COLUMNS_WIDE = 5;
const MAX_THUMBNAILS = 4; // slot 0 is the hero; show slots 1-4 in grid

enum PreviewSize {
  HERO = "hero",
  THUMB = "thumb",
}

/**
 * MacroResponseBlockNode
 *
 * Dedicated canvas node for the response_block subtype in the Macros module.
 * Renders hero attachment + description text + thumbnail grid for multi-file nodes.
 * Extracted from MessageBlockNode to keep macro-specific concerns separate.
 */
const MacroResponseBlockNode: FunctionComponent<CustomNodeProps> = (props) => {
  const { nodeData, leftHandlerStyle, isConnectable, editNode, onHover } = props;

  const channel = nodeData?.backendConfig?.channel as MacroChannelKey | undefined;
  const isWhatsApp =
    channel === MacroChannelKey.WHATSAPP ||
    channel === MacroChannelKey.MESSENGER ||
    channel === MacroChannelKey.LIVECHAT;
  const isInstagramOrEmail =
    channel === MacroChannelKey.INSTAGRAM ||
    channel === MacroChannelKey.EMAIL;

  const attachments: any[] = nodeData?.attachments ?? [];
  const hasDescription = !!nodeData?.description;
  const hasAttachments = attachments.length > 0;

  const showEmptyState = !hasDescription && !hasAttachments;
  const showHeroImage = isWhatsApp && hasAttachments;
  const showDescription = hasDescription;
  const showDivider = isWhatsApp
    ? attachments.length > 1
    : hasDescription && hasAttachments;
  const showThumbnailGrid = isWhatsApp
    ? attachments.length > 1
    : hasAttachments;

  const thumbnailItems = isWhatsApp
    ? attachments.slice(1, 1 + MAX_THUMBNAILS)
    : attachments;

  const onClickEdit = () => {
    editNode(
      nodeData?.type,
      props?.id,
      NodeSubType.RESPONSE_BLOCK,
      nodeData?.nodeIndex,
      nodeData?.automationChannel
    );
  };

  const renderAttachmentPreview = (attachment: any, size: PreviewSize) => {
    const type = attachment?.type as AttachmentType | undefined;
    const url = attachment?.mediaUrl as string | undefined;

    if (type === AttachmentType.VIDEO) {
      return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <video style={{ width: "100%", height: "100%", objectFit: "cover" }} preload="none">
            <source src={url} type="video/mp4" />
          </video>
          <VideoIconOverlay>
            <VideoIcon
              width={size === PreviewSize.HERO ? 48 : 24}
              height={size === PreviewSize.HERO ? 48 : 24}
              color={COLORS.content.secondary}
            />
          </VideoIconOverlay>
        </div>
      );
    }

    if (type === AttachmentType.DOCUMENT) {
      return (
        <DocumentPreview>
          <DocumentIcon
            width={size === PreviewSize.HERO ? 48 : 24}
            height={size === PreviewSize.HERO ? 48 : 24}
            color="#616161"
          />
        </DocumentPreview>
      );
    }

    return (
      <ResizableImage
        imageUrl={url ?? ""}
        targetSize={size === PreviewSize.HERO ? 256 : 64}
        style={{ objectFit: "cover", width: "100%", height: "100%" }}
      />
    );
  };

  return (
    <MessageNodeStyle subType={NodeSubType.RESPONSE_BLOCK} channel={nodeData?.automationChannel ?? ""}>
      {!showEmptyState && (
        <Handle
          id="target"
          type="target"
          position={Position.Left}
          style={leftHandlerStyle}
          isConnectable={isConnectable}
        />
      )}

      <NodeStyle
        className="customNode flex--column"
        onMouseEnter={() => onHover(props?.id, NodeSubType.RESPONSE_BLOCK)}
        onMouseLeave={() => onHover(null, undefined)}
        nodeColor={backgroundBasedOnSubType(NodeSubType.RESPONSE_BLOCK, nodeData?.automationChannel ?? "")}
        onClick={onClickEdit}
      >
        {/* Header */}
        <div className="customNode__header flex justify-content-between align-items-center">
          <div className="flex">
            <span className="customNode__header__icon icon-wrapper" style={{ borderRadius: "4px" }}>
              {IconMapping(NodeSubType.RESPONSE_BLOCK, nodeData?.automationChannel ?? "")}
            </span>
            <NodeIdHeader nodeId={nodeData?.nodeId} />
            <div style={{ width: 8 }} />
            <span className="customNode__header__title">
              {nodeData?.title || "Response"}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="customNode__body flex--column">
          {showEmptyState && (
            <AddActionButton>Add message</AddActionButton>
          )}

          {!showEmptyState && (
            <>
              {/* Main container: hero + description */}
              {(showHeroImage || showDescription) && (
                <AttachmentGridContainer>
                  {showHeroImage && (
                    <AttachmentMainImage>
                      {renderAttachmentPreview(attachments[0], PreviewSize.HERO)}
                    </AttachmentMainImage>
                  )}
                  {showDescription && (
                    <div className="customNode__body__description-message">
                      <SmallRegular numberOfLines={3}>
                        {nodeData.description}
                      </SmallRegular>
                    </div>
                  )}
                </AttachmentGridContainer>
              )}

              {/* Divider */}
              {showDivider && <AttachmentDivider />}

              {/* Thumbnail grid */}
              {showThumbnailGrid && thumbnailItems.length > 0 && (
                <AttachmentGridContainer>
                  <AttachmentThumbnailGrid columns={isInstagramOrEmail ? MAX_THUMBNAIL_COLUMNS_WIDE : MAX_THUMBNAIL_COLUMNS}>
                    {thumbnailItems.map((attachment, idx) => (
                      <AttachmentThumbnail key={`${attachment.mediaUrl}-${idx}`}>
                        {renderAttachmentPreview(attachment, PreviewSize.THUMB)}
                      </AttachmentThumbnail>
                    ))}
                  </AttachmentThumbnailGrid>
                </AttachmentGridContainer>
              )}
            </>
          )}
        </div>

        {/* Next step handle */}
        <ChildNodeContainer>
          <ChildNodeText>Next step</ChildNodeText>
          <div>
            <Handle
              type="source"
              position={Position.Right}
              id="source"
              style={sourceHandleStyle}
              isConnectable={isConnectable}
            />
          </div>
        </ChildNodeContainer>
      </NodeStyle>
    </MessageNodeStyle>
  );
};

export default MacroResponseBlockNode;
